import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LogOut, User, MessageSquare, FileText, Loader2, Activity, Brain, Maximize2, Minimize2
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { profileAPI, chatAPI } from '../services/api';
import ProfileTab from '../components/ProfileTab';
import MedicalRecordsTab from '../components/MedicalRecordsTab';
import HealthStatsTab from '../components/HealthStatsTab';
import ChatInterface from '../components/ChatInterface';
import PsychologistPage from '../components/PsychologistPage';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenState, setFullscreenState] = useState('normal'); // 'normal', 'entering', 'fullscreen', 'exiting'
  const tabsRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({});

  // Tab configuration - Profile moved to the end, Data tab removed
  const tabs = [
    { id: 'chat', icon: MessageSquare, label: 'Чат', hasFullscreen: true },
    { id: 'psychologist', icon: Brain, label: 'Психолог', hasFullscreen: true },
    { id: 'medical', icon: FileText, label: 'Карта' },
    { id: 'stats', icon: Activity, label: 'Стат.' },
    { id: 'profile', icon: User, label: 'Профиль' },
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }
    loadUserData();
  }, [navigate]);

  // Animate tab indicator - with delay for initial render
  useEffect(() => {
    const updateIndicator = () => {
      if (tabsRef.current) {
        const activeButton = tabsRef.current.querySelector(`[data-tab="${activeTab}"]`);
        if (activeButton) {
          const rect = activeButton.getBoundingClientRect();
          const parentRect = tabsRef.current.getBoundingClientRect();
          setIndicatorStyle({
            left: rect.left - parentRect.left,
            width: rect.width,
          });
        }
      }
    };
    
    // Run immediately for tab changes
    updateIndicator();
    
    // Also run after a short delay for initial mount
    const timer = setTimeout(updateIndicator, 100);
    return () => clearTimeout(timer);
  }, [activeTab, loading]);

  const loadUserData = async () => {
    try {
      const userData = await profileAPI.getProfile();
      setUser(userData);
      
      try {
        const sessions = await chatAPI.getSessions();
        if (sessions && sessions.length > 0) {
          const lastSessionId = localStorage.getItem('lastChatSessionId');
          const existingSession = sessions.find(s => s.id === lastSessionId);
          setSessionId(existingSession ? lastSessionId : sessions[0].id);
        } else {
          const session = await chatAPI.createSession();
          setSessionId(session.id);
        }
      } catch (sessionError) {
        console.error('Error loading sessions:', sessionError);
        const session = await chatAPI.createSession();
        setSessionId(session.id);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      toast({ title: 'Ошибка загрузки', description: 'Не удалось загрузить данные пользователя', variant: 'destructive' });
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      navigate('/auth');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    toast({ title: 'Выход выполнен', description: 'До скорой встречи' });
    navigate('/auth');
  };

  // Smooth fullscreen handlers with proper animation states
  const handleFullscreen = () => {
    setFullscreenState('entering');
    // Longer animation - 800ms for smooth feel
    setTimeout(() => {
      setIsFullscreen(true);
      setFullscreenState('fullscreen');
    }, 800);
  };

  const handleMinimize = () => {
    setFullscreenState('exiting');
    setTimeout(() => {
      setIsFullscreen(false);
      setFullscreenState('normal');
    }, 800);
  };
  
  // Get dynamic styles for fullscreen animation
  const getFullscreenStyles = (isCurrentTab) => {
    if (!isCurrentTab) return {};
    
    if (fullscreenState === 'entering') {
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999,
        background: '#1c1c1e',
        animation: 'smoothExpand 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards'
      };
    }
    
    if (fullscreenState === 'fullscreen') {
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999,
        borderRadius: '0px',
        background: '#1c1c1e'
      };
    }
    
    if (fullscreenState === 'exiting') {
      return {
        position: 'relative',
        animation: 'smoothCollapse 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards'
      };
    }
    
    return {};
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl border border-white/[0.1] flex items-center justify-center" style={{ background: 'rgba(28, 28, 30, 0.95)' }}>
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
          <p className="text-white/50 font-medium">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: '#0a0a12' }}>
      {/* 
        === Solid Dark Background — 2026 High-Performance Glass ===
        No ambient orbs, no noise overlays, no heavy filters.
        Glass depth is achieved purely via inset highlights + gradients on the cards themselves.
        Result: 60 FPS locked, instant paint, no pixelation.
      */}

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          
          {/* visionOS Liquid Glass Tab Navigation */}
          <div className="mb-5 sm:mb-6">
            <div 
              ref={tabsRef}
              className="liquid-tab-bar relative w-full p-1.5 sm:p-2"
            >
              {/* Animated Liquid Glass indicator */}
              <div 
                className="liquid-tab-indicator absolute top-1.5 sm:top-2 h-[calc(100%-12px)] sm:h-[calc(100%-16px)]"
                style={{
                  left: indicatorStyle.left || 0,
                  width: indicatorStyle.width || 0,
                }}
              />
              
              <div className="relative grid grid-cols-5 gap-0.5">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    data-tab={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`${tab.id}-tab-trigger`}
                    className={`relative flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 
                      py-2.5 sm:py-3 px-1.5 sm:px-3 rounded-full
                      transition-all duration-300 ease-out
                      ${activeTab === tab.id 
                        ? 'text-white' 
                        : 'text-white/50 hover:text-white/70'
                      }`}
                  >
                    <tab.icon className={`w-4 h-4 sm:w-[18px] sm:h-[18px] flex-shrink-0 transition-transform duration-300 ${activeTab === tab.id ? 'scale-110' : ''}`} />
                    <span className="text-[8px] sm:text-xs font-medium leading-none mt-0.5 sm:mt-0">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Chat Tab - SINGLE instance, smooth fullscreen animation */}
          <TabsContent value="chat" className="mt-0" forceMount style={{ display: activeTab === 'chat' ? 'block' : 'none' }}>
            <div 
              className={`chat-container overflow-hidden ${
                fullscreenState === 'fullscreen' || fullscreenState === 'entering'
                  ? ''
                  : 'glass-card-dark h-[calc(100vh-130px)] sm:h-[calc(100vh-150px)]'
              } ${fullscreenState === 'entering' ? 'fullscreen-entering' : ''} ${fullscreenState === 'exiting' ? 'fullscreen-exiting' : ''}`}
              style={getFullscreenStyles(activeTab === 'chat')}
            >
              {sessionId && (
                <ChatInterface 
                  sessionId={sessionId} 
                  onFullscreen={handleFullscreen} 
                  onMinimize={handleMinimize}
                  isFullscreen={(fullscreenState === 'fullscreen' || fullscreenState === 'entering') && activeTab === 'chat'}
                />
              )}
            </div>
          </TabsContent>

          {/* Psychologist Tab - SINGLE instance, smooth fullscreen animation */}
          <TabsContent value="psychologist" className="mt-0" data-testid="psychologist-tab-content">
            <div 
              className={`chat-container overflow-hidden ${
                fullscreenState === 'fullscreen' || fullscreenState === 'entering'
                  ? ''
                  : 'glass-card-dark h-[calc(100vh-130px)] sm:h-[calc(100vh-150px)]'
              } ${fullscreenState === 'entering' ? 'fullscreen-entering' : ''} ${fullscreenState === 'exiting' ? 'fullscreen-exiting' : ''}`}
              style={getFullscreenStyles(activeTab === 'psychologist')}
            >
              <PsychologistPage 
                onFullscreen={handleFullscreen} 
                onMinimize={handleMinimize}
                isFullscreen={(fullscreenState === 'fullscreen' || fullscreenState === 'entering') && activeTab === 'psychologist'}
              />
            </div>
          </TabsContent>

          {/* Medical Records Tab */}
          <TabsContent value="medical" className="mt-0">
            <div className="glass-card-dark p-5 sm:p-8">
              <MedicalRecordsTab />
            </div>
          </TabsContent>

          {/* Health Stats Tab */}
          <TabsContent value="stats" className="mt-0">
            <div className="glass-card-dark p-5 sm:p-8">
              <HealthStatsTab />
            </div>
          </TabsContent>

          {/* Profile Tab - moved to the end */}
          <TabsContent value="profile" className="mt-0">
            <div className="glass-card-dark p-5 sm:p-8">
              <div className="flex items-center justify-between mb-6 sm:mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-white">Профиль</h2>
                </div>
                <Button onClick={handleLogout} className="glass-button-dark text-white/70 hover:text-white">
                  <LogOut className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Выйти</span>
                </Button>
              </div>
              <ProfileTab user={user} onUserUpdate={setUser} />
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Apple Liquid Glass 2026 — Solid-BG High-Performance Glass (no backdrop-filter, 60 FPS locked) */}
      <style jsx global>{`
        /*
          === GLASS CARD — solid-bg fast glass ===
          No backdrop-filter (nothing to blur on solid BG + saves GPU).
          Depth via: tinted fill + rim-light border + 3-layer inset shadows + top sheen line.
        */
        .glass-card-dark {
          position: relative;
          background:
            linear-gradient(
              145deg,
              rgba(255, 255, 255, 0.055) 0%,
              rgba(255, 255, 255, 0.02) 50%,
              rgba(255, 255, 255, 0.01) 100%
            ),
            rgba(22, 24, 34, 0.85);
          border: 1px solid rgba(255, 255, 255, 0.09);
          border-radius: 28px;
          box-shadow:
            inset 0 1px 0 0 rgba(255, 255, 255, 0.12),
            inset 0 -1px 0 0 rgba(0, 0, 0, 0.3),
            0 12px 28px -8px rgba(0, 0, 0, 0.55),
            0 2px 4px rgba(0, 0, 0, 0.25);
          overflow: hidden;
          transition: border-color 0.25s ease, box-shadow 0.25s ease;
        }
        /* Hairline top-edge highlight — the tell-tale sign of real glass */
        .glass-card-dark::before {
          content: '';
          position: absolute;
          top: 0;
          left: 12%;
          right: 12%;
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.35),
            transparent
          );
          pointer-events: none;
          z-index: 1;
        }

        .chat-container {
          overflow: hidden;
          border-radius: 28px;
        }
        .chat-container > * {
          position: relative;
          z-index: 2;
        }

        /* Glass button — pill with rim light */
        .glass-button-dark {
          position: relative;
          background:
            linear-gradient(
              180deg,
              rgba(255, 255, 255, 0.1) 0%,
              rgba(255, 255, 255, 0.03) 100%
            ),
            rgba(40, 42, 54, 0.85);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 9999px;
          padding: 10px 18px;
          font-weight: 500;
          box-shadow:
            inset 0 1px 0 0 rgba(255, 255, 255, 0.18),
            inset 0 -1px 0 0 rgba(0, 0, 0, 0.25),
            0 4px 10px rgba(0, 0, 0, 0.3);
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .glass-button-dark:hover {
          background:
            linear-gradient(
              180deg,
              rgba(255, 255, 255, 0.14) 0%,
              rgba(255, 255, 255, 0.04) 100%
            ),
            rgba(54, 56, 68, 0.9);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateY(-1px);
          box-shadow:
            inset 0 1px 0 0 rgba(255, 255, 255, 0.25),
            inset 0 -1px 0 0 rgba(0, 0, 0, 0.25),
            0 6px 14px rgba(0, 0, 0, 0.35);
        }

        /* Glass input — translucent pill */
        .glass-input-dark {
          background:
            linear-gradient(
              180deg,
              rgba(255, 255, 255, 0.04) 0%,
              rgba(255, 255, 255, 0.015) 100%
            ),
            rgba(40, 42, 54, 0.7);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 9999px;
          box-shadow:
            inset 0 1px 2px rgba(0, 0, 0, 0.25),
            inset 0 1px 0 rgba(255, 255, 255, 0.08);
          transition: all 0.2s ease;
        }
        .glass-input-dark:focus {
          background:
            linear-gradient(
              180deg,
              rgba(255, 255, 255, 0.06) 0%,
              rgba(255, 255, 255, 0.02) 100%
            ),
            rgba(40, 42, 54, 0.85);
          border-color: rgba(10, 132, 255, 0.5);
          box-shadow:
            inset 0 1px 2px rgba(0, 0, 0, 0.25),
            0 0 0 3px rgba(10, 132, 255, 0.18);
        }
      `}</style>
    </div>
  );
};

export default DashboardPage;
